import React from 'react';

interface Notification {
  id: string;
  type: 'success' | 'warning' | 'error' | 'info';
  message: string;
  timestamp: string;
  read: boolean;
}

interface NotificationCenterProps {
  notifications: Notification[];
  onMarkAsRead: (id: string) => void;
  onDismiss: (id: string) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ 
  notifications, 
  onMarkAsRead, 
  onDismiss 
}) => {
  
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'success':
        return 'bi-check-circle-fill';
      case 'warning':
        return 'bi-exclamation-triangle-fill';
      case 'error':
        return 'bi-x-circle-fill';
      case 'info':
      default:
        return 'bi-info-circle-fill';
    }
  };

  const getTypeClass = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-danger';
      case 'info':
      default:
        return 'text-info';
    }
  };

  // Contar las notificaciones no leídas
  const unreadCount = notifications.filter(n => !n.read).length;

  if (notifications.length === 0) {
    return (
      <div className="text-center p-3">
        <p className="text-muted">No hay notificaciones</p>
      </div>
    );
  }

  return (
    <div className="notification-center">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Notificaciones</h6>
        {unreadCount > 0 && (
          <span className="badge bg-primary">{unreadCount} nuevas</span>
        )}
      </div>
      
      <div className="notification-list" style={{ maxHeight: '350px', overflowY: 'auto' }}>
        {notifications.map((notification) => (
          <div 
            key={notification.id} 
            className={`notification-item card mb-2 ${notification.read ? 'border-light' : 'border-primary'}`}
          >
            <div className="card-body p-2">
              <div className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <i className={`bi ${getTypeIcon(notification.type)} ${getTypeClass(notification.type)} me-2`}></i>
                  <div>
                    <div className={notification.read ? 'text-muted' : 'fw-bold'}>
                      {notification.message}
                    </div>
                    <small className="text-muted">
                      {formatTimestamp(notification.timestamp)}
                    </small>
                  </div>
                </div>
                
                <div className="notification-actions">
                  {!notification.read && (
                    <button 
                      className="btn btn-sm btn-link p-0 me-2" 
                      onClick={() => onMarkAsRead(notification.id)}
                      title="Marcar como leída"
                    >
                      <i className="bi bi-check-lg"></i>
                    </button>
                  )}
                  <button 
                    className="btn btn-sm btn-link text-danger p-0" 
                    onClick={() => onDismiss(notification.id)}
                    title="Descartar"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {notifications.length > 5 && (
        <div className="text-center mt-2">
          <button className="btn btn-sm btn-outline-secondary">
            Ver todas las notificaciones
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
